<template>
  <div
    ref="rootRef"
    class="move-select-wrap"
    :class="[
      `move-select-wrap--${size}`,
      { 'move-select-wrap--block': block, 'move-select-wrap--open': isOpen }
    ]"
  >
    <button
      :id="id"
      ref="triggerRef"
      type="button"
      class="move-select-trigger"
      :class="{ 'is-disabled': disabled, 'is-placeholder': !hasSelection }"
      :disabled="disabled"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span v-if="prefixIcon" class="material-icons move-select-trigger__prefix">{{ prefixIcon }}</span>
      <span class="move-select-trigger__label">{{ displayLabel }}</span>
      <span class="material-icons move-select-trigger__chevron" :class="{ 'is-open': isOpen }">expand_more</span>
    </button>

    <Teleport to="body">
      <Transition name="move-select-fade">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="move-select-menu"
          :style="menuStyle"
          role="listbox"
          :aria-labelledby="id"
        >
          <button
            v-for="option in options"
            :key="String(option.value)"
            type="button"
            role="option"
            class="move-select-option"
            :class="{
              'is-selected': isSelected(option.value),
              'is-disabled': option.disabled
            }"
            :disabled="option.disabled"
            @click="selectOption(option)"
          >
            <span class="material-icons move-select-option__check">check</span>
            <span class="move-select-option__label">{{ option.label }}</span>
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, null],
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select…'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: undefined
  },
  prefixIcon: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'compact', 'inline'].includes(value)
  },
  block: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const rootRef = ref(null)
const triggerRef = ref(null)
const menuRef = ref(null)
const isOpen = ref(false)
const menuStyle = ref({})

const hasSelection = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    return false
  }
  return props.options.some((option) => isSelected(option.value))
})

const displayLabel = computed(() => {
  const selected = props.options.find((option) => isSelected(option.value))
  return selected?.label ?? props.placeholder
})

const isSelected = (value) => String(value) === String(props.modelValue)

const updateMenuPosition = () => {
  const trigger = triggerRef.value
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const viewportPadding = 12
  const menuMaxHeight = 280
  const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
  const spaceAbove = rect.top - viewportPadding
  const openUpward = spaceBelow < 180 && spaceAbove > spaceBelow

  menuStyle.value = {
    position: 'fixed',
    left: `${Math.max(viewportPadding, Math.min(rect.left, window.innerWidth - rect.width - viewportPadding))}px`,
    minWidth: `${rect.width}px`,
    maxWidth: `${Math.min(420, window.innerWidth - viewportPadding * 2)}px`,
    zIndex: 10050,
    ...(openUpward
      ? {
          bottom: `${window.innerHeight - rect.top + 6}px`,
          maxHeight: `${Math.min(menuMaxHeight, spaceAbove)}px`
        }
      : {
          top: `${rect.bottom + 6}px`,
          maxHeight: `${Math.min(menuMaxHeight, spaceBelow)}px`
        })
  }
}

const close = () => {
  isOpen.value = false
}

const open = async () => {
  if (props.disabled) return
  isOpen.value = true
  await nextTick()
  updateMenuPosition()
}

const toggle = () => {
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

const selectOption = (option) => {
  if (option.disabled) return
  emit('update:modelValue', option.value)
  emit('change', option.value)
  close()
}

const onTriggerKeydown = (event) => {
  if (event.key === 'Escape') {
    close()
    return
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    toggle()
  }
  if (event.key === 'ArrowDown' && !isOpen.value) {
    event.preventDefault()
    open()
  }
}

const onDocumentMouseDown = (event) => {
  if (!isOpen.value) return
  const target = event.target
  if (rootRef.value?.contains(target) || menuRef.value?.contains(target)) return
  close()
}

const onViewportChange = () => {
  if (!isOpen.value) return
  updateMenuPosition()
}

watch(isOpen, (openState) => {
  if (openState) {
    document.addEventListener('mousedown', onDocumentMouseDown)
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
  } else {
    document.removeEventListener('mousedown', onDocumentMouseDown)
    window.removeEventListener('resize', onViewportChange)
    window.removeEventListener('scroll', onViewportChange, true)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMouseDown)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.material-icons {
  font-family: 'Material Icons', sans-serif;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  display: inline-block;
}
</style>
